import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CurrencyDollar, Plus, PencilSimple, Trash, MagnifyingGlass, ArrowUp, ArrowDown, ArrowsDownUp, Export } from "@phosphor-icons/react";
import { useTableFeatures, exportCSV } from "@/hooks/useTableFeatures";

const statusColors = {
  pending: "bg-[#FFD100]/10 text-[#946B00] border-[#FFD100]/20",
  approved: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
  paid: "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20",
  denied: "bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00]/20",
};
const fmt = (n) => (!n || n === 0) ? "$0" : `$${Number(n).toLocaleString()}`;

const default472 = {
  client_id: "", client_name: "", ben: "", frn: "", funding_year: "",
  invoice_number: "", invoice_amount: 0, disbursement_amount: 0,
  service_start_date: "", service_end_date: "", status: "pending", notes: "",
};
const default474 = {
  client_id: "", client_name: "", spin: "", frn: "", funding_year: "",
  invoice_number: "", invoice_amount: 0, authorized_disbursement: 0,
  service_provider: "", status: "pending", notes: "",
};

function InvoiceTable({ items, type, onEdit, onDelete }) {
  const is472 = type === "472";
  return (
    <div className="border border-gray-200 bg-white">
      <Table>
        <TableHeader>
          <TableRow className="data-table-header">
            <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Client</TableHead>
            <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">{is472 ? "BEN" : "SPIN"}</TableHead>
            <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">FRN</TableHead>
            <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">FY</TableHead>
            <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Invoice #</TableHead>
            <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Amount</TableHead>
            <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">{is472 ? "Disbursed" : "Auth. Disb."}</TableHead>
            <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Status</TableHead>
            <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold w-[80px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#6B7280] text-sm">No records</TableCell></TableRow>
          ) : items.map((item) => (
            <TableRow key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
              <TableCell className="py-3 px-4 text-[#1A1D20] font-medium">{item.client_name || "—"}</TableCell>
              <TableCell className="py-3 px-4 font-mono text-xs">{is472 ? (item.ben || "—") : (item.spin || "—")}</TableCell>
              <TableCell className="py-3 px-4 font-mono text-xs text-[#002FA7]">{item.frn || "—"}</TableCell>
              <TableCell className="py-3 px-4 text-sm">{item.funding_year || "—"}</TableCell>
              <TableCell className="py-3 px-4 text-sm">{item.invoice_number || "—"}</TableCell>
              <TableCell className="py-3 px-4 text-sm font-medium">{fmt(item.invoice_amount)}</TableCell>
              <TableCell className="py-3 px-4 text-sm font-medium text-[#00B341]">{fmt(is472 ? item.disbursement_amount : item.authorized_disbursement)}</TableCell>
              <TableCell className="py-3 px-4"><Badge className={`rounded-none text-xs border capitalize ${statusColors[item.status] || statusColors.pending}`}>{item.status}</Badge></TableCell>
              <TableCell className="py-3 px-4">
                <div className="flex items-center gap-1">
                  <button data-testid={`edit-${type}-${item.id}`} onClick={() => onEdit(item)} className="p-1.5 hover:bg-gray-100"><PencilSimple size={14} className="text-[#6B7280]" /></button>
                  <button data-testid={`delete-${type}-${item.id}`} onClick={() => onDelete(item.id)} className="p-1.5 hover:bg-red-50"><Trash size={14} className="text-[#FF2A00]" /></button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function Invoices() {
  const [tab, setTab] = useState("472");
  const [items472, setItems472] = useState([]);
  const [items474, setItems474] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...default472 });
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SH = ({ k, children }) => (
    <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold cursor-pointer select-none hover:text-[#1A1D20]" onClick={() => toggleSort(k)}>
      <div className="flex items-center gap-1">{children}{sortKey === k ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowsDownUp size={10} className="opacity-30" />}</div>
    </TableHead>
  );

  useEffect(() => { load472(); load474(); loadClients(); }, []);
  const load472 = async () => { try { const { data } = await api.get("/forms/472"); setItems472(data); } catch {} };
  const load474 = async () => { try { const { data } = await api.get("/forms/474"); setItems474(data); } catch {} };
  const loadClients = async () => { try { const { data } = await api.get("/clients"); setClients(data); } catch {} };

  const save = async () => {
    try {
      const path = tab === "472" ? "/forms/472" : "/forms/474";
      if (editing) { await api.put(`${path}/${editing}`, form); toast.success("Updated"); }
      else { await api.post(path, form); toast.success("Created"); }
      setOpen(false);
      tab === "472" ? load472() : load474();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    try {
      await api.delete(`/forms/${tab}/${id}`);
      toast.success("Deleted");
      tab === "472" ? load472() : load474();
    } catch { toast.error("Failed"); }
  };

  const edit = (item) => { setForm({ ...item }); setEditing(item.id); setOpen(true); };
  const openNew = () => { setForm(tab === "472" ? { ...default472 } : { ...default474 }); setEditing(null); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectClient = (id) => { const c = clients.find((cl) => cl.id === id); setForm((f) => ({ ...f, client_id: id, client_name: c ? c.name : "" })); };

  const currentItems = tab === "472" ? items472 : items474;
  const fundingYears = [...new Set([...items472, ...items474].map((i) => i.funding_year).filter(Boolean))].sort().reverse();
  let filtered = currentItems.filter((i) => JSON.stringify(i).toLowerCase().includes(search.toLowerCase()));
  if (statusFilter !== "all") filtered = filtered.filter((i) => i.status === statusFilter);
  if (yearFilter !== "all") filtered = filtered.filter((i) => i.funding_year === yearFilter);
  if (sortKey) {
    filtered.sort((a, b) => {
      const av = a[sortKey] ?? "", bv = b[sortKey] ?? "";
      const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }
  const is472 = tab === "472";
  const csvCols472 = [{ label: "Client", key: "client_name" },{ label: "BEN", key: "ben" },{ label: "FRN", key: "frn" },{ label: "FY", key: "funding_year" },{ label: "Invoice #", key: "invoice_number" },{ label: "Amount", key: "invoice_amount" },{ label: "Disbursed", key: "disbursement_amount" },{ label: "Status", key: "status" }];
  const csvCols474 = [{ label: "Client", key: "client_name" },{ label: "SPIN", key: "spin" },{ label: "FRN", key: "frn" },{ label: "FY", key: "funding_year" },{ label: "Invoice #", key: "invoice_number" },{ label: "Amount", key: "invoice_amount" },{ label: "Auth Disbursement", key: "authorized_disbursement" },{ label: "Status", key: "status" }];

  return (
    <div className="p-6" data-testid="invoices-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">Financials</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Invoices & Disbursements</h1>
          <p className="text-sm text-[#6B7280] mt-1">FCC Form 472 (BEAR) & Form 474 (SPI)</p>
        </div>
        <button data-testid="add-invoice-btn" onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
          <Plus size={16} weight="bold" /> Add {is472 ? "BEAR" : "SPI"}
        </button>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setSearch(""); }} className="mb-4">
        <TabsList className="rounded-none bg-transparent border border-gray-200 p-0 h-auto">
          <TabsTrigger data-testid="tab-472" value="472" className="rounded-none px-4 py-2 text-sm data-[state=active]:bg-[#002FA7] data-[state=active]:text-white">Form 472 (BEAR)</TabsTrigger>
          <TabsTrigger data-testid="tab-474" value="474" className="rounded-none px-4 py-2 text-sm data-[state=active]:bg-[#002FA7] data-[state=active]:text-white">Form 474 (SPI)</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input data-testid="search-invoices" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-none w-[120px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="rounded-none"><SelectItem value="all">All Status</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="denied">Denied</SelectItem></SelectContent>
        </Select>
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger data-testid="filter-invoice-year" className="rounded-none w-[140px]"><SelectValue placeholder="All Years" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All Years</SelectItem>
            {fundingYears.map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}
          </SelectContent>
        </Select>
        <button data-testid="export-invoices-btn" onClick={() => exportCSV(filtered, is472 ? csvCols472 : csvCols474, `${is472 ? "form472" : "form474"}.csv`)} className="ml-auto flex items-center gap-1.5 bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-xs transition-colors">
          <Export size={14} /> Export CSV
        </button>
      </div>

      <InvoiceTable items={filtered} type={tab} onEdit={edit} onDelete={remove} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{editing ? "Edit" : "Add"} {is472 ? "Form 472 (BEAR)" : "Form 474 (SPI)"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Client</label>
              <Select value={form.client_id} onValueChange={selectClient}>
                <SelectTrigger data-testid="invoice-client-select" className="rounded-none"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent className="rounded-none max-h-[200px]">{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">{is472 ? "BEN" : "SPIN"}</label>
                <Input data-testid="invoice-id-input" value={is472 ? form.ben : form.spin} onChange={(e) => set(is472 ? "ben" : "spin", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">FRN</label>
                <Input data-testid="invoice-frn-input" value={form.frn} onChange={(e) => set("frn", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Funding Year</label>
                <Input data-testid="invoice-fy-input" value={form.funding_year} onChange={(e) => set("funding_year", e.target.value)} className="rounded-none" />
              </div>
            </div>
            {!is472 && (
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Service Provider</label>
                <Input data-testid="invoice-provider-input" value={form.service_provider} onChange={(e) => set("service_provider", e.target.value)} className="rounded-none" />
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Invoice #</label>
                <Input data-testid="invoice-number-input" value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Invoice Amount ($)</label>
                <Input data-testid="invoice-amount-input" type="number" value={form.invoice_amount} onChange={(e) => set("invoice_amount", parseFloat(e.target.value) || 0)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">{is472 ? "Disbursement ($)" : "Auth. Disbursement ($)"}</label>
                <Input data-testid="invoice-disbursement-input" type="number" value={is472 ? form.disbursement_amount : form.authorized_disbursement} onChange={(e) => set(is472 ? "disbursement_amount" : "authorized_disbursement", parseFloat(e.target.value) || 0)} className="rounded-none" />
              </div>
            </div>
            {is472 && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Service Start</label>
                  <Input data-testid="invoice-start-input" type="date" value={form.service_start_date} onChange={(e) => set("service_start_date", e.target.value)} className="rounded-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Service End</label>
                  <Input data-testid="invoice-end-input" type="date" value={form.service_end_date} onChange={(e) => set("service_end_date", e.target.value)} className="rounded-none" />
                </div>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Status</label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="invoice-status-select" className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none"><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="denied">Denied</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Notes</label>
              <textarea data-testid="invoice-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="cancel-invoice-btn" onClick={() => setOpen(false)} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm">Cancel</button>
            <button data-testid="save-invoice-btn" onClick={save} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
